
import React, { useState, useEffect, useRef } from "react";
import { apiGet } from "../../services/api";
import { useToast } from "../common/ToastProvider";
import "./PatientSearch.css";
import BackendRequired from "../common/BackendRequired";

export interface PatientResult {
  patient_id: string | number;
  name: string;
  gender?: string;
  mobile?: string;
  age?: number;
  area?: string;
  medical_alerts?: string;
}

interface PatientSearchProps {
  onSelect: (patient: PatientResult) => void;
}

export default function PatientSearch({ onSelect }: PatientSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PatientResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setShowDropdown(false);
      setError(null);
      return;
    }

    setLoading(true);
    setShowDropdown(true);
    setSelectedIndex(-1);

    const timer = setTimeout(async () => {
      try {
        const data = await apiGet(`/api/patients/search?q=${encodeURIComponent(trimmed)}`) as PatientResult[];
        setResults(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error("Search error:", err);
        setResults([]);
        setError("Unable to search right now. Check server connection.");
        showToast({ message: "Search failed. Check connection.", type: "error" });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleSelect = (patient: PatientResult) => {
    onSelect(patient);
    setQuery("");
    setResults([]);
    setShowDropdown(false);
    setError(null);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setShowDropdown(false);
    setError(null);
  };

  return (
    <div className="patient-search-wrapper" ref={searchRef}>
      <BackendRequired showDisabled fallback={
        <div className="search-input-container disabled">
          <span className="search-icon">🚫</span>
          <input
            type="text"
            className="search-input-field"
            placeholder="Search unavailable (Offline)"
            disabled
          />
        </div>
      }>
        <div className="search-input-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input-field"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setShowDropdown(true)}
            placeholder="Search patient (Name, ID, Mobile)..."
            autoComplete="off"
          />

          {loading && <div className="spinner-small"></div>}

          {!loading && query && (
            <button
              className="btn-clear"
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </BackendRequired>

      {showDropdown && query.length >= 2 && (
        <>
          <div className="search-backdrop" onClick={clearSearch} />
          <div className="search-dropdown">
          {loading ? (
            <div className="search-status">Searching patient database...</div>
          ) : error ? (
            <div className="search-status no-results">
              <p>⚠️ {error}</p>
              <small>If you are running locally, ensure FastAPI is running on port 8001.</small>
            </div>
          ) : results.length > 0 ? (
            <ul className="search-list">
              {results.map((p, index) => (
                <li
                  key={p.patient_id}
                  className={`search-item ${index === selectedIndex ? "active" : ""}`}
                  onClick={() => handleSelect(p)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="item-avatar">
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="item-info">
                    <div className="item-name">
                      {p.name}
                      {p.gender && <span className="item-badge">{p.gender}</span>}
                    </div>
                    <div className="item-meta">
                      📱 {p.mobile || p.patient_id} • Age: {p.age ?? "N/A"} • {p.area || "No Area"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="search-status no-results">
              <p>No patients found for &quot;<strong>{query}</strong>&quot;</p>
              <small>Ask reception to register new patient.</small>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}
